import {Button, IconButton, useTranslationContext} from 'platform/components';

import {SECTION_BORDER_BOTTOM} from '../../constants/borderStyles';
import {SELECTION_TEST_ID_PREFIX} from '../../constants/selectionColumn';
import {ActionDefinitionObject} from '../../types/ActionDefinition';
import {toRegisteredIcon} from '../../utils/toRegisteredIcon';

interface BulkActionsBarProps {
  selectedCount: number;
  bulkActions: ActionDefinitionObject;
  onDeselectAll: () => void;
  onBulkAction: (actionKey: string) => void;
  disableAllActions?: boolean;
  isLoading?: boolean;
  /**
   * `inline` (default) renders the current light bar, mounted in the grid's own
   * layout flow and animated by the parent's AnimatePresence.
   * `floating` renders a dark, rounded, fixed-position bar for mobile — the parent
   * (BulkActionSection) portals it to document.body without a motion wrapper, since
   * motion transforms on an ancestor break position:fixed descendants.
   */
  variant?: 'inline' | 'floating';
}

/**
 * Shows: selected count text | bulk action buttons | deselect IconButton.
 *
 * When no bulk actions are available, degrades gracefully to count + deselect only.
 */
export function BulkActionsBar(props: BulkActionsBarProps) {
  const t = useTranslationContext();

  const actionEntries = Object.entries(props.bulkActions);

  if (props.variant === 'floating') {
    return (
      <div
        className="fixed left-3.5 right-3.5 bottom-16 z-(--z-header-popper) flex items-center gap-2 px-3 py-2.5 rounded-2xl bg-[#091E42] text-white shadow-xl"
        data-testid={`${SELECTION_TEST_ID_PREFIX}-bulk-bar`}
      >
        <IconButton
          icon="navigation/close"
          size="small"
          priority="secondary"
          aria-label={t('page.datagrid.button.deselectAll')}
          onClick={props.onDeselectAll}
          data-testid={`${SELECTION_TEST_ID_PREFIX}-deselect-all`}
        />
        <span className="text-[14px] font-semibold whitespace-nowrap flex-shrink-0">
          {t('page.datagrid.labels.numberOfSelectedRows', {
            selectedRows: String(props.selectedCount),
          })}
        </span>
        <div className="flex items-center gap-1 overflow-x-auto min-w-0">
          {actionEntries.map(([actionKey, actionDef]) => (
            <Button
              key={actionKey}
              // Ghost variant sets no explicit text/icon color, so it inherits the
              // ancestor's `text-white`. Other transparent variants (e.g. ghostLink)
              // hardcode a theme color not guaranteed to read on this dark navy bg.
              variant="ghost"
              size="small"
              title={actionDef.title}
              leftIcon={toRegisteredIcon(actionDef.icon.value)}
              onClick={() => props.onBulkAction(actionKey)}
              isDisabled={props.disableAllActions || props.isLoading}
              isLoading={props.isLoading}
              data-testid={`${SELECTION_TEST_ID_PREFIX}-bulk-${actionKey}`}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-center justify-between px-2 py-2 bg-gray-50 text-sm text-gray-600 flex-shrink-0"
      style={SECTION_BORDER_BOTTOM}
      data-testid={`${SELECTION_TEST_ID_PREFIX}-bulk-bar`}
    >
      <span>
        {t('page.datagrid.labels.numberOfSelectedRows', {
          selectedRows: String(props.selectedCount),
        })}
      </span>

      <div className="flex items-center gap-1">
        {actionEntries.map(([actionKey, actionDef]) => (
          <Button
            key={actionKey}
            variant="secondary"
            size="small"
            title={actionDef.title}
            leftIcon={toRegisteredIcon(actionDef.icon.value)}
            onClick={() => props.onBulkAction(actionKey)}
            isDisabled={props.disableAllActions || props.isLoading}
            isLoading={props.isLoading}
            data-testid={`${SELECTION_TEST_ID_PREFIX}-bulk-${actionKey}`}
          />
        ))}
        <IconButton
          icon="navigation/close"
          size="small"
          priority="secondary"
          aria-label={t('page.datagrid.button.deselectAll')}
          onClick={props.onDeselectAll}
          data-testid={`${SELECTION_TEST_ID_PREFIX}-deselect-all`}
        />
      </div>
    </div>
  );
}
